"use client";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { createContext, useContext, useEffect, useState } from "react";
import { darkTheme, lightTheme } from "./theme";

type ThemeContextType = {
  isDarkMode: boolean;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: true,
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/** 写 theme cookie，maxAge 1 年 */
function setThemeCookie(theme: "dark" | "light") {
  document.cookie = `theme=${theme};path=/;max-age=31536000;SameSite=Lax`;
}

export function ThemeProvider({
  children,
  initialTheme = "dark",
}: {
  children: React.ReactNode;
  initialTheme?: "dark" | "light";
}) {
  const [isDarkMode, setIsDarkMode] = useState(initialTheme === "dark");

  // 客户端首次挂载：如果 localStorage 有值但 cookie 还没同步，以 localStorage 为准并写 cookie
  useEffect(() => {
    try {
      const saved = localStorage.getItem("theme");
      if (saved === "dark" || saved === "light") {
        const dark = saved === "dark";
        setIsDarkMode(dark);
        setThemeCookie(saved);
        document.documentElement.setAttribute("data-theme", saved);
      }
    } catch {
      // SSR or localStorage unavailable
    }
  }, []);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newTheme = !prev;
      const themeStr = newTheme ? "dark" : "light";
      localStorage.setItem("theme", themeStr);
      setThemeCookie(themeStr);
      document.documentElement.setAttribute("data-theme", themeStr);
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <MUIThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
}
